import { Injectable, inject } from '@angular/core';
import { ActivatedRouteSnapshot, RouterStateSnapshot, TitleStrategy } from '@angular/router';
import { APP_NAME, PageInfoService } from '../services/page-info.service';

/** Sentinel route `title` value: tells `RouteInfoTitleStrategy` that this route (or
 *  one of its descendants) sets `heading` dynamically — route-based titles for the
 *  whole chain are ignored and left for the routed component to set itself. */
export const DYNAMIC_TITLE = '?';

/**
 * Resolves `PageInfoService.heading`/`description` from the route tree on every
 * navigation, replacing Angular's `DefaultTitleStrategy` (which would otherwise also
 * write `document.title` from `route.title` and race with `PageInfoService`'s own
 * effect). `PageInfoService` remains the single writer of the real `document.title`.
 */
@Injectable({
  providedIn: 'root',
})
export class RouteInfoTitleStrategy extends TitleStrategy {
  private readonly pageInfo = inject(PageInfoService);

  override updateTitle(snapshot: RouterStateSnapshot): void {
    const chain: ActivatedRouteSnapshot[] = [];
    for (let route: ActivatedRouteSnapshot | null = snapshot.root; route; route = route.firstChild) {
      chain.push(route);
    }
    const leafToRoot = [...chain].reverse();
    const hasDynamicTitle = leafToRoot.some((route) => route.title === DYNAMIC_TITLE);

    if (!hasDynamicTitle) {
      const titles = leafToRoot
        .map((route) => route.title)
        .filter((title): title is string => !!title);
      this.pageInfo.heading.set(titles.length ? titles.join(' - ') : APP_NAME);
    }
    // else: some route in the chain opted out (DYNAMIC_TITLE) — its component sets heading itself.

    const summary = leafToRoot
      .map((route) => route.data['summary'] as string | undefined)
      .find((value): value is string => !!value);
    this.pageInfo.description.set(summary ?? '');
  }
}
